import type {
  SubjectPageId,
  SubjectPagePath,
} from '../tuitionSubjects/subjectPages.js';
import { SCHOOL_SUBJECT_ELIGIBILITY } from './schoolSubjectEligibility.js';

export type SchoolSubject = 'english' | 'chemistry';
export interface SchoolSubjectPathway {
  id: string;
  label: string;
  years: readonly number[];
  coursePath: SubjectPagePath;
  contentId: SubjectPageId;
}

const ENGLISH_PATHWAYS = {
  national: {
    id: 'national',
    label: 'National secondary English',
    years: [1, 2, 3, 4],
    coursePath: '/english',
    contentId: 'english',
  },
  g1: {
    id: 'g1',
    label: 'G1 English',
    years: [1, 2, 3, 4],
    coursePath: '/english/g1',
    contentId: 'english-g1',
  },
  ip: {
    id: 'ip',
    label: 'IP English and Language Arts',
    years: [1, 2, 3, 4],
    coursePath: '/english/ip',
    contentId: 'english-ip',
  },
  'ib-school': {
    id: 'ib-school',
    label: 'School-based English before the IB Diploma',
    years: [1, 2, 3, 4],
    coursePath: '/english/ib',
    contentId: 'english-ib',
  },
} as const satisfies Record<string, SchoolSubjectPathway>;

const CHEMISTRY_PATHWAYS = {
  pure: {
    id: 'pure',
    label: 'G3 Pure Chemistry',
    years: [3, 4],
    coursePath: '/chemistry/pure-chemistry',
    contentId: 'pure-chemistry',
  },
  combined: {
    id: 'combined',
    label: 'Science (Chemistry)',
    years: [3, 4],
    coursePath: '/chemistry/combined-science',
    contentId: 'combined-science',
  },
  ip: {
    id: 'ip',
    label: 'IP Chemistry',
    years: [3, 4],
    coursePath: '/chemistry/ip',
    contentId: 'chemistry-ip',
  },
} as const satisfies Record<string, SchoolSubjectPathway>;

export interface SchoolSubjectPageDescriptor {
  slug: string;
  subject: SchoolSubject;
  label: 'English' | 'Chemistry';
  routePath: `/schools/${string}/${SchoolSubject}`;
  pathways: readonly SchoolSubjectPathway[];
}

const reviews = new Map(SCHOOL_SUBJECT_ELIGIBILITY.map(row => [row[0], row]));

/** One publication boundary for the router, HTTP status, sitemap and directories. */
export function resolveSchoolSubjectPage(
  slug: string,
  subject: string
): SchoolSubjectPageDescriptor | null {
  const row = reviews.get(slug);
  if (!row || (subject !== 'english' && subject !== 'chemistry')) return null;
  const pathways: readonly SchoolSubjectPathway[] =
    subject === 'english'
      ? row[1].map(id => ENGLISH_PATHWAYS[id])
      : row[2].map(id => CHEMISTRY_PATHWAYS[id]);
  if (!pathways.length) return null;
  return {
    slug,
    subject,
    label: subject === 'english' ? 'English' : 'Chemistry',
    routePath: `/schools/${slug}/${subject}`,
    pathways,
  };
}

export function resolveSchoolSubjectRoute(
  path: string
): SchoolSubjectPageDescriptor | null {
  const match =
    /^\/schools\/([a-z0-9]+(?:-[a-z0-9]+)*)\/(english|chemistry)$/.exec(path);
  return match ? resolveSchoolSubjectPage(match[1], match[2]) : null;
}

export function listSchoolSubjectPages(): SchoolSubjectPageDescriptor[] {
  return SCHOOL_SUBJECT_ELIGIBILITY.flatMap(([slug]) =>
    (['english', 'chemistry'] as const).flatMap(subject => {
      const page = resolveSchoolSubjectPage(slug, subject);
      return page ? [page] : [];
    })
  );
}
